import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import CustomPopover from "../../Popover";
import Tooltip from "../../Tooltip";
import {
  MagnifyingGlassIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ChannelAccount, Entity, ProvisionalPost } from "../../../types";
import findEntitiesInfo from "./actions/EntitiesFetcher";
import { useDebounce } from "../../../utils/various";
import { ChannelLogo } from "../../ChannelSelector";
import LoadingUI from "../../LoadingUI";

const MentionSelector = ({
  showSelector,
  channelAccounts,
  insert,
  post,
  setGlobalMentions,
}: {
  showSelector: boolean;
  channelAccounts: ChannelAccount[];
  insert: (inserted: string, lenght?: number) => void;
  post: ProvisionalPost;
  setGlobalMentions: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, Entity>>>
  >;
}) => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const uniqChannelAccounts = useMemo(() => {
    const seenChannels = new Set<ChannelAccount["channel"]["identifier"]>();

    return channelAccounts.reduce<ChannelAccount[]>((acc, ca) => {
      if (
        // À SUPPRIMER => ca.channel.identifier === "linkedin" &&
        // QUAND LES AUTRES CANAUX AURONT ÉGALEMENT LA
        // FONCTIONNALITÉ DES MENTIONS
        ca.channel.identifier === "linkedin" &&
        !seenChannels.has(ca.channel.identifier)
      ) {
        seenChannels.add(ca.channel.identifier);
        acc.push(ca);
      }
      return acc;
    }, []);
  }, [channelAccounts]);

  const [selectedChannel, setSelectedChannel] = useState<ChannelAccount>(
    uniqChannelAccounts[0]
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentCollab, setCurrentCollab] = useState<string>("");
  const [entities, setEntities] = useState<Entity[]>([]);

  const submitMention = async (value: string) => {
    if (!value?.trim()) {
      return;
    }

    setLoading(true);
    setError(null);
    setEntities([]);

    try {
      const response = await findEntitiesInfo(value, selectedChannel);
      if (response.entities) {
        setEntities(response.entities);
      } else {
        setError("Aucun collaborateur trouvé pour ce nom.");
      }
    } catch (err) {
      setError("Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  const debounce = useDebounce(250);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCurrentCollab(value);
    if (value.length > 2) {
      debounce(() => submitMention(value));
    }
  };

  const insertMention = (mention: Entity) => {
    const thisPOC = post?.post_on_channels_attributes?.find(
      (poc) => poc.channel_account_id === selectedChannel.id
    );

    const index = Object.keys(
      thisPOC?.channel_specific_data?.mentions || {}
    ).length;

    const mentionKey = `$MENTION${index + 1}`;

    // On ajoute les mentions dans chaque channel_specific_data
    post.post_on_channels_attributes.forEach((poc) => {
      if (poc.channel_account_id === selectedChannel.id) {
        poc.channel_specific_data = {
          ...poc.channel_specific_data,
          mentions: {
            ...poc.channel_specific_data?.mentions,
            [mentionKey]: {
              ...mention,
            },
          },
        };
      } else {
        poc.channel_specific_data = {
          ...poc.channel_specific_data,
          mentions: {
            ...poc.channel_specific_data?.mentions,
            [mentionKey]: {
              name: mention.name,
            },
          },
        };
      }
    });
    // On ajoute les mentions dans la mention globale
    channelAccounts.forEach((ca) => {
      setGlobalMentions((mentions) => {
        const key = ca.channel.identifier;
        return {
          ...mentions,
          [key]: {
            [mentionKey]:
              ca.id === selectedChannel.id
                ? mention
                : {
                    name: mention.name,
                  },
          },
        };
      });
    });

    insert(`${mentionKey}`, mention.name.length + 1); // +1 for arobase
  };

  // Check if the current domain is from 'safti'
  const isSaftiDomain = window.location.hostname.includes("safti");

  if (isSaftiDomain) {
    // If the domain includes 'safti', don't render the mention button
    return null;
  }

  return (
    <CustomPopover
      disabled={!showSelector}
      button={
        <Tooltip text={intl.formatMessage({ id: "body_editor.mention_collaborator", defaultMessage: "Mentionner un collaborateur" })}>
          <div
            id="mention-button"
            className={`btn-small-round ${
              showSelector
                ? "cursor-pointer"
                : "cursor-default hover:bg-gray-200"
            }`}
          >
            <UserCircleIcon
              className={`size-6 ${
                showSelector
                  ? "hover:text-brand_darker"
                  : "text-gray-400 hover:text-gray-400"
              }`}
            />
          </div>
        </Tooltip>
      }
      position="top"
      panelClasses="drop-shadow-sm shadow-md rounded-md"
    >
      <div
        className="z-50 bg-white my-auto relative rounded-md border p-2 w-[300px]"
        ref={ref}
        style={{ zIndex: 1000 }}
      >
        <div className="flex flex-col justify-center h-full">
          <div className="flex w-full relative gap-0">
            {selectedChannel &&
              uniqChannelAccounts.map((ca) => (
                <button
                  type="button"
                  key={ca.id}
                  className={`flex items-center w-fit p-1 cursor-pointer ${
                    ca.id === selectedChannel.id
                      ? `border-b-2 border-opacity-40 border-${ca.channel.identifier}`
                      : "border-b"
                  }`}
                  style={
                    ca.id === selectedChannel.id ? { zIndex: 2 } : undefined
                  }
                  onClick={() => {
                    if (ca.id === selectedChannel.id) return;
                    setSelectedChannel(ca);
                    setCurrentCollab("");
                  }}
                >
                  <ChannelLogo
                    identifier={ca?.channel?.identifier}
                    active={ca?.channel?.id === selectedChannel?.channel?.id}
                    size={4}
                  />
                </button>
              ))}
          </div>
          <div className="flex flex-col gap-2 pt-4 pb-1 bg-white relative">
            {(loading || entities.length > 0) && (
              <div className="m-0">
                <div className="max-h-[150px] w-full overflow-y-auto !bg-white z-50 rounded-b-md">
                  <div className="mb-4">
                    {loading && (
                      <div className="w-full h-full flex justify-center items-center">
                        <LoadingUI />
                      </div>
                    )}
                    {entities.map((entity) => (
                      <button
                        key={entity.name}
                        type="button"
                        onClick={() => insertMention(entity)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            insertMention(entity);
                          }
                        }}
                        className="w-full flex items-center gap-2 p-2 hover:bg-gray-100"
                      >
                        {entity.picture_url && (
                          <div className="w-[20%]">
                            <div className="w-8 h-8 rounded-full border border-gray-200 bg-gray-300">
                              <img
                                src={entity.picture_url}
                                alt={"avatar"}
                                className="w-full h-full object-cover rounded-full"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).classList.add(
                                    "hidden"
                                  );
                                }}
                              />
                            </div>
                          </div>
                        )}
                        <div className="w-[80%]">
                          <p className="block text-left truncate">
                            {entity.name}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className={`flex items-center relative`}>
              <input
                type="text"
                value={currentCollab}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitMention((e.target as HTMLInputElement).value);
                  }
                }}
                className={`peer py-1 pl-2 pr-16 w-full text-sm form-input flex flex-1 rounded-md focus:ring focus:ring-opacity-20 ${
                  error
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-brand_focus focus:ring-brand_focus"
                }`}
                placeholder="squeezie"
              />
              {currentCollab && (
                <button
                  type="button"
                  className="absolute right-8 top-1/2 translate-y-[-50%]"
                  onClick={() => {
                    setCurrentCollab("");
                    setEntities([] as any);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setCurrentCollab("");
                      setEntities([] as any);
                    }
                  }}
                >
                  <XMarkIcon className="size-4 text-gray-400 hover:text-brand_main" />
                </button>
              )}
              <button
                type="button"
                onClick={(e) =>
                  submitMention((e.target as HTMLInputElement).value)
                }
                className="peer-focus:border-brand_focus peer-focus:text-brand_main absolute flex items-center right-0 h-full w-8 p-1 font-medium bg-gray-50 rounded-r-md border border-gray-300 hover:bg-gray-100 duration-300 text-gray-500"
              >
                {loading ? (
                  <LoadingUI />
                ) : (
                  <MagnifyingGlassIcon className="inline m-auto h-4 w-4" />
                )}
              </button>
            </div>
            {error ? (
              <div
                role="alert"
                className="border border-red-100 text-sm rounded bg-red-50 px-1 py-1 text-red-700"
              >
                <p>{error}</p>
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    </CustomPopover>
  );
};

export default MentionSelector;
